import { movies } from '@/mocks/movies';
import { reviews } from '@/mocks/reviews';
import { reviewsMore } from '@/mocks/reviewsMore';
import { specials } from '@/mocks/specials';
import { news, newsCategories } from '@/mocks/news';
import type { Movie, NewsItem, Review, Special } from '@/types';

export { specials, news, newsCategories };

export const allReviews: Review[] = [...reviews, ...reviewsMore];

export const allDirectors: string[] = Array.from(
  new Set(movies.map((m) => m.director)),
);

export const allGenres: string[] = Array.from(
  new Set(movies.flatMap((m) => m.genres)),
);

export const allYears: number[] = Array.from(
  new Set(movies.map((m) => m.year)),
).sort((a, b) => b - a);

export const getMovieById = (id: string): Movie | undefined =>
  movies.find((m) => m.id === id);

export const getNewsById = (id: string): NewsItem | undefined =>
  news.find((n) => n.id === id);

export const getSpecialById = (id: string): Special | undefined =>
  specials.find((s) => s.id === id);

export const getReviewsByMovie = (movieId: string): Review[] =>
  allReviews.filter((r) => r.movieId === movieId);

export const getReviewById = (id: string): Review | undefined =>
  allReviews.find((r) => r.id === id);

export const getMoviesByIds = (ids: string[]): Movie[] =>
  ids.map((id) => getMovieById(id)).filter((m): m is Movie => Boolean(m));

export const getReviewsByIds = (ids: string[]): Review[] =>
  ids.map((id) => getReviewById(id)).filter((r): r is Review => Boolean(r));

export const getSpecialForMovie = (movieId: string): Special | undefined =>
  specials.find((s) => s.movieIds.includes(movieId));

export const getRelatedMovies = (movie: Movie, limit = 3): Movie[] => {
  const scored = movies
    .filter((m) => m.id !== movie.id)
    .map((m) => {
      let score = 0;
      if (m.director === movie.director) score += 3;
      score += m.genres.filter((g) => movie.genres.includes(g)).length * 2;
      score += m.tags.filter((t) => movie.tags.includes(t)).length;
      return { m, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || b.m.rating - a.m.rating);
  return scored.slice(0, limit).map((x) => x.m);
};

export const getRelatedNews = (item: NewsItem, limit = 4): NewsItem[] =>
  news
    .filter((n) => n.id !== item.id && n.category === item.category)
    .slice(0, limit);

export interface SearchResult {
  type: 'movie' | 'review' | 'news';
  id: string;
  title: string;
  subtitle: string;
  to: string;
}

export const searchAll = (rawQuery: string): SearchResult[] => {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return [];
  const results: SearchResult[] = [];

  movies.forEach((m) => {
    const haystack = [m.title, m.titleEn, m.director, ...m.cast, ...m.genres, ...m.tags]
      .join(' ')
      .toLowerCase();
    if (haystack.includes(q)) {
      results.push({
        type: 'movie',
        id: m.id,
        title: m.title,
        subtitle: `${m.year} · ${m.director} · ${m.genres.join(' / ')}`,
        to: `/detail/${m.id}`,
      });
    }
  });

  allReviews.forEach((r) => {
    const movie = getMovieById(r.movieId);
    const haystack = [r.title, r.author, r.excerpt, movie?.title]
      .join(' ')
      .toLowerCase();
    if (haystack.includes(q)) {
      results.push({
        type: 'review',
        id: r.id,
        title: r.title,
        subtitle: `影评 · ${r.author} · ${r.date}`,
        to: `/detail/${r.movieId}`,
      });
    }
  });

  news.forEach((n) => {
    const haystack = [n.title, n.category, n.excerpt, n.author]
      .join(' ')
      .toLowerCase();
    if (haystack.includes(q)) {
      results.push({
        type: 'news',
        id: n.id,
        title: n.title,
        subtitle: `资讯 · ${n.category} · ${n.date}`,
        to: `/news/${n.id}`,
      });
    }
  });

  return results.slice(0, 12);
};

export const formatDuration = (min: number): string => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h} 小时 ${m} 分钟` : `${m} 分钟`;
};